/**
 * Secure Script Loader
 *
 * Provides utilities for securely loading external scripts with authorisation
 * validation and integrity checks. All external scripts on payment pages should
 * be loaded through this module.
 *
 * @see authorisedScripts.ts for the allowlist of authorised scripts
 * @see SCRIPT_INVENTORY.md for documentation of each script
 */

import { isAuthorisedScript, type ScriptType, getCurrentEnvironment } from "./authorisedScripts";

export interface SecureScriptOptions {
  /** Optional SRI hash for integrity verification */
  integrity?: string;
  /** CORS setting for the script */
  crossOrigin?: "anonymous" | "use-credentials";
  /** Whether to load the script asynchronously (default: true) */
  async?: boolean;
  /** Whether to defer script execution (default: false) */
  defer?: boolean;
  /** Callback when script loads successfully */
  onLoad?: () => void;
  /** Callback when script fails to load */
  onError?: (error: Error) => void;
  /** Optional script type attribute (e.g. "module") */
  type?: string;
}

export class ScriptAuthorisationError extends Error {
  constructor(
    public readonly url: string,
    public readonly scriptType: ScriptType,
  ) {
    super(`Unauthorised script blocked: ${url} (type: ${scriptType})`);
    this.name = "ScriptAuthorisationError";
  }
}

/**
 * Securely loads an external script after validating it against the authorised scripts registry.
 *
 * @param url - The URL of the script to load
 * @param scriptType - The type of script being loaded (for authorisation validation)
 * @param options - Optional configuration for script loading
 * @returns Promise that resolves when the script is loaded, or rejects on error
 * @throws ScriptAuthorisationError if the script URL is not authorised
 */
export async function loadAuthorisedScript(
  url: string,
  scriptType: ScriptType,
  options: SecureScriptOptions = {},
): Promise<HTMLScriptElement> {
  // Step 1: Validate the script URL against the authorised patterns
  if (!isAuthorisedScript(url, scriptType)) {
    const error = new ScriptAuthorisationError(url, scriptType);
    console.error(`[SecureScriptLoader] ${error.message}`);
    options.onError?.(error);
    throw error;
  }

  // Step 2: Create and configure the script element
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = url;
    if (options.type) {
      script.type = options.type;
    }
    script.async = options.async ?? true;

    if (options.defer) {
      script.defer = true;
    }

    // Apply SRI hash if provided
    if (options.integrity) {
      script.integrity = options.integrity;
      script.crossOrigin = options.crossOrigin ?? "anonymous";
    } else if (options.crossOrigin) {
      script.crossOrigin = options.crossOrigin;
    }

    // Set up load handler
    script.onload = () => {
      options.onLoad?.();
      resolve(script);
    };

    // Set up error handler
    script.onerror = () => {
      const error = new Error(`Failed to load ${scriptType} script from ${url}`);
      console.error(`[SecureScriptLoader] ${error.message}`);
      options.onError?.(error);
      reject(error);
    };

    // Step 3: Append to document to trigger loading
    document.body.appendChild(script);
  });
}

/**
 * Loads the FreedomPay SDK script securely.
 *
 * @param url - The FreedomPay SDK URL (fetched from backend)
 * @param options - Optional configuration
 * @returns Promise that resolves when the script is loaded
 */
export async function loadFreedomPayScript(
  url: string,
  options: Omit<SecureScriptOptions, "integrity"> = {},
): Promise<HTMLScriptElement> {
  return loadAuthorisedScript(url, "freedomPay", {
    ...options,
    // FreedomPay scripts update frequently, so SRI is not used
    // Security is enforced via CSP headers and URL pattern validation
  });
}

/**
 * Loads the Google reCAPTCHA script securely.
 *
 * @param captchaKey - The reCAPTCHA site key
 * @param options - Optional configuration
 * @returns Promise that resolves when the script is loaded
 */
export async function loadRecaptchaScript(
  captchaKey: string,
  options: Omit<SecureScriptOptions, "integrity"> = {},
): Promise<HTMLScriptElement> {
  // Validate captcha key format before constructing URL
  if (!captchaKey || typeof captchaKey !== "string" || !/^[a-zA-Z0-9_-]+$/.test(captchaKey)) {
    const error = new Error("Invalid reCAPTCHA key format");
    console.error(`[SecureScriptLoader] ${error.message}`);
    options.onError?.(error);
    throw error;
  }

  const url = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(captchaKey)}`;

  return loadAuthorisedScript(url, "recaptcha", {
    ...options,
    // Google reCAPTCHA scripts update frequently, so SRI is not used
    // Security is enforced via CSP headers and URL pattern validation
  });
}

/**
 * Loads the OpenComponents (OC) Client script securely.
 *
 * @param options - Optional configuration
 * @returns Promise that resolves when the script is loaded
 */
export async function loadOcClientScript(
  options: Omit<SecureScriptOptions, "integrity"> = {},
): Promise<HTMLScriptElement> {
  const env = getCurrentEnvironment();
  const url =
    env === "prod"
      ? "https://mfe.workspace.accessacloud.com/registry/oc-client/client.js"
      : "https://mfe.test.workspace.accessacloud.com/registry/oc-client/client.js";

  if (isScriptLoaded(url)) {
    return document.querySelector(`script[src="${url}"]`) as HTMLScriptElement;
  }

  return loadAuthorisedScript(url, "ocClient", {
    ...options,
  });
}

/**
 * Checks if a script with the given URL is already loaded in the document.
 *
 * @param url - The script URL to check
 * @returns true if a script with this URL is already in the document
 */
export function isScriptLoaded(url: string): boolean {
  const scripts = document.getElementsByTagName("script");
  for (const script of scripts) {
    if (script.src === url) {
      return true;
    }
  }
  return false;
}

/**
 * Loads the DMN Auth Components script securely.
 *
 * @param version - The version string for the auth components script
 * @param options - Optional configuration
 * @returns Promise that resolves when the script is loaded
 * @throws Error if the version format is invalid
 */
export async function loadAuthComponentsScript(
  version: string,
  options: Omit<SecureScriptOptions, "integrity"> = {},
): Promise<HTMLScriptElement> {
  if (!version || !/^[a-z0-9][a-z0-9.-]*$/.test(version) || version.includes("..")) {
    const error = new Error("Invalid auth components version format");
    console.error(`[SecureScriptLoader] ${error.message}`);
    options.onError?.(error);
    throw error;
  }

  const url = `https://auth-components.designmynight.com/${version}/dmn-auth-components.js`;

  if (isScriptLoaded(url)) {
    const existingScript = document.querySelector<HTMLScriptElement>(`script[src="${url}"]`);
    if (existingScript) {
      return existingScript;
    }
  }

  return loadAuthorisedScript(url, "authComponents", {
    ...options,
    type: "module",
  });
}

/**
 * Removes a script from the document by URL.
 * Useful for cleanup when components unmount.
 *
 * @param url - The script URL to remove
 * @returns true if a script was removed, false if not found
 */
export function removeScript(url: string): boolean {
  const scripts = document.getElementsByTagName("script");
  for (const script of scripts) {
    if (script.src === url) {
      script.parentNode?.removeChild(script);
      return true;
    }
  }
  return false;
}
